import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Badge } from '../../components/ui';
import './profileSection.css';

/**
 * One section module for every profile drawer: a heading with a count and
 * an action slot, then rows that open something.
 *
 * Kyu, 9 September: "It's always going to be the same thing for any drawers
 * like this, most recent to the oldest, top to bottom." The ORDER is the
 * caller's job (newestFirst in profileLinks.ts); this component only draws
 * what it is given, in the order it is given, so that rule lives in one
 * place and not in every drawer.
 *
 * Used by the deal drawer first (drop 1). The lead panel and the company
 * panel take the same component in later drops, which is the modularity
 * Kyu asked for: one Contacts, one CG orders, one Activity, three homes.
 */
export function ProfileSection({
  title,
  count,
  hint,
  actions,
  children,
}: {
  title: string;
  count?: number;
  /** A short grey phrase after the count: what the list is drawn from. */
  hint?: string;
  /** Buttons or links on the right of the heading. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="profile-section">
      <div className="profile-section-head">
        <h3 className="profile-section-title">{title}</h3>
        {count === undefined ? null : <span className="profile-section-count">{count}</span>}
        {hint === undefined ? null : <span className="profile-section-hint">{hint}</span>}
        {actions === undefined ? null : <div className="profile-section-actions">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export type ProfileTone = 'success' | 'warning' | 'brand' | 'neutral' | 'danger';

/**
 * A row: a name that opens the thing, a grey line under it, and something
 * on the right (a status pill and a date, usually).
 */
export function ProfileRow({
  name,
  href,
  sub,
  pill,
  when,
}: {
  name: ReactNode;
  /** Where the name goes. A row with nowhere to go is plain text. */
  href?: string;
  sub?: ReactNode;
  pill?: { label: string; tone: ProfileTone };
  when?: string;
}) {
  return (
    <div className="profile-row">
      <div className="profile-row-name">
        {href === undefined ? name : <Link to={href}>{name}</Link>}
      </div>
      {sub === undefined ? null : <div className="profile-row-sub">{sub}</div>}
      {pill === undefined && when === undefined ? null : (
        <div className="profile-row-right">
          {pill === undefined ? null : <Badge tone={pill.tone}>{pill.label}</Badge>}
          {when === undefined ? null : <span className="profile-row-when">{when}</span>}
        </div>
      )}
    </div>
  );
}

export function ProfileEmpty({ children }: { children: ReactNode }) {
  return <p className="profile-empty">{children}</p>;
}
